import { F32, U32, U8 } from '../engine/typed-arrays.js';
import { TU, BU } from '../engine/gpu-flags.js';
import { createEmptyPickingInfo } from './picking-info.js';
import { createPickingRay } from './ray.js';
import { mat4Invert } from '../math/mat4-invert.js';
import { getPickingSceneBGL } from './picking-scene-bgl.js';
import { getViewProjectionMatrix, getCameraPosition } from '../camera/camera.js';
import { resolveCameraViewport } from '../camera/viewport.js';
import { createUniformBuffer, createEmptyUniformBuffer, createMappedBuffer } from '../resource/gpu-buffers.js';

const _pickVP = new F32(20);
const PICK_MESH_UBO_BYTES = 80;
const _uboScratch = new ArrayBuffer(PICK_MESH_UBO_BYTES);
const _uboF32 = new F32(_uboScratch);
const _uboU32 = new U32(_uboScratch);
const _uboView = new U8(_uboScratch);
function createGpuPicker(scene) {
  return {
    _detailedPicking: false,
    _device: null,
    _scene: scene,
    _rt: null,
    _sceneUbo: null,
    _sceneBG: null,
    _contributors: null,
    _pending: null
  };
}
function ensurePickerDevice(engine, picker) {
  if (picker._device === engine._device) {
    return;
  }
  if (picker._device) {
    disposePicker(picker);
  }
  picker._device = engine._device;
}
function ensureTargets(engine, picker) {
  const device = engine._device;
  if (picker._rt) {
    return picker._rt;
  }
  const colorTex = device.createTexture({ label: "pick-color", size: [1, 1], format: "rgba8unorm", usage: TU.RENDER_ATTACHMENT | TU.COPY_SRC });
  const depthColorTex = device.createTexture({
    label: "pick-depth-color",
    size: [1, 1],
    format: "r32float",
    usage: TU.RENDER_ATTACHMENT | TU.COPY_SRC
  });
  const depthTex = device.createTexture({ label: "pick-depth", size: [1, 1], format: "depth24plus", usage: TU.RENDER_ATTACHMENT });
  picker._rt = {
    colorTex,
    colorView: colorTex.createView(),
    depthColorTex,
    depthColorView: depthColorTex.createView(),
    detail: null,
    depthTex,
    depthView: depthTex.createView(),
    colorStaging: device.createBuffer({ label: "pick-color-staging", size: 256, usage: BU.COPY_DST | BU.MAP_READ }),
    depthStaging: device.createBuffer({ label: "pick-depth-staging", size: 256, usage: BU.COPY_DST | BU.MAP_READ })
  };
  return picker._rt;
}
function ensureSceneUbo(engine, picker) {
  const device = engine._device;
  if (!picker._sceneUbo) {
    picker._sceneUbo = createEmptyUniformBuffer(engine, 80, "pick-scene-ubo");
    const sceneBGL = getPickingSceneBGL(engine);
    picker._sceneBG = device.createBindGroup({ label: "pick-scene-bg", layout: sceneBGL, entries: [{ binding: 0, resource: { buffer: picker._sceneUbo } }] });
  }
  return picker._sceneUbo;
}
function computePickVP(out, vp, sampleX, sampleY, w, h) {
  const ndcX = 2 * sampleX / w - 1;
  const ndcY = 1 - 2 * sampleY / h;
  for (let c = 0; c < 4; c++) {
    const base = c * 4;
    const w3 = vp[base + 3];
    out[base] = w * (vp[base] - ndcX * w3);
    out[base + 1] = h * (vp[base + 1] - ndcY * w3);
    out[base + 2] = vp[base + 2];
    out[base + 3] = w3;
  }
}
function createPickDiscardBindGroup(engine, layout, discard, mesh, tempBuffers) {
  const storage = discard.storage;
  if (!storage || storage.length === 0) {
    return null;
  }
  const entries = [];
  for (let i = 0; i < storage.length; i++) {
    const data = storage[i].data(mesh);
    if (!data) {
      return null;
    }
    const buffer = createMappedBuffer(engine, data, BU.STORAGE, "pick-discard-storage");
    tempBuffers.push(buffer);
    entries.push({ binding: i, resource: { buffer } });
  }
  const device = engine._device;
  return device.createBindGroup({
    label: `pick-discard-${discard.key}-bg`,
    layout,
    entries
  });
}
async function pickAsyncImpl(picker, x, y, options) {
  const scene = picker._scene;
  const pickFilter = options?.filter ?? null;
  const pickDiscard = options?.discard ?? null;
  const ignored = options?.ignore;
  const debugLabel = options?.debugLabel;
  const engine = scene.surface.engine;
  if (!scene.camera) {
    return createEmptyPickingInfo();
  }
  ensurePickerDevice(engine, picker);
  const device = engine._device;
  const detailed = picker._detailedPicking;
  const preparedContributors = [];
  if (!pickFilter && scene._pickSources.length > 0) {
    const sources = scene._pickSources.slice();
    for (const source of sources) {
      let contributor = picker._contributors?.get(source);
      if (!contributor) {
        const pipeline = await source.load();
        if (!scene._pickSources.includes(source)) {
          continue;
        }
        contributor = pipeline.createPickContributor(source.entity);
        (picker._contributors ??= /* @__PURE__ */ new Map()).set(source, contributor);
      }
      preparedContributors.push({ source, contributor });
    }
  }
  let needsDeformation = false;
  let needsAdvancedPipeline = !!pickDiscard?.worldAdjustWgsl || !!pickDiscard?.vertexData || !!pickDiscard?.storage?.some((storage) => storage.vertex);
  let candidates;
  if (ignored) {
    const prepared = (await import('./picking-ignore.js')).prepareIgnoredCandidates(scene.meshes, ignored, pickFilter, needsAdvancedPipeline);
    candidates = prepared.candidates;
    needsDeformation = prepared.deformed;
    needsAdvancedPipeline = prepared.advanced;
  } else {
    candidates = [];
    for (const mesh of scene.meshes) {
      if (mesh.pickable !== false && (!pickFilter || pickFilter(mesh))) {
        candidates.push({ mesh, ignore: null });
        needsDeformation ||= !!(mesh.morphTargets || mesh.skeleton);
        needsAdvancedPipeline ||= !!mesh.vat || !!mesh.thinInstances || !!mesh._gpu._vbLayout?._p;
      }
    }
  }
  const deformProjection = needsDeformation && !needsAdvancedPipeline ? await import('./deform-picking-projection.js') : null;
  const deformedVertex = needsDeformation && detailed ? await import('./deformed-vertex.js') : null;
  const detailedPicking = detailed ? await import('./detailed-picking.js') : null;
  const debug = debugLabel ? await import('./picking-debug.js') : null;
  const advancedDraw = needsAdvancedPipeline ? await (await import('./picking-advanced-draw.js')).prepareAdvancedDraw(engine, candidates) : null;
  const pipelineApi = advancedDraw ? null : detailed ? await import('./picking-detailed-pipeline.js') : await import('./picking-pipeline.js');
  if (engine._device !== device) {
    return pickAsyncImpl(picker, x, y, options);
  }
  const canvas = scene.surface.canvas;
  const camera = scene.camera;
  if (!camera) {
    return createEmptyPickingInfo();
  }
  const backingWidth = canvas.width;
  const backingHeight = canvas.height;
  const clientWidth = ("clientWidth" in canvas ? canvas.clientWidth : 0) || backingWidth;
  const clientHeight = ("clientHeight" in canvas ? canvas.clientHeight : 0) || backingHeight;
  const scaleX = backingWidth / clientWidth;
  const scaleY = backingHeight / clientHeight;
  const pickX = x * scaleX;
  const pickY = y * scaleY;
  const viewport = resolveCameraViewport(camera, backingWidth, backingHeight);
  const w = viewport.width;
  const h = viewport.height;
  if (w === 0 || h === 0) {
    return createEmptyPickingInfo();
  }
  if (pickX < viewport.x || pickY < viewport.y || pickX >= viewport.x + viewport.width || pickY >= viewport.y + viewport.height) {
    return createEmptyPickingInfo();
  }
  const px = Math.max(0, Math.min(Math.floor(pickX - viewport.x), w - 1));
  const py = Math.max(0, Math.min(Math.floor(pickY - viewport.y), h - 1));
  const sampleX = pickX - viewport.x;
  const sampleY = pickY - viewport.y;
  const pixelCenterX = px + 0.5;
  const pixelCenterY = py + 0.5;
  const aspect = w / h;
  const vp = getViewProjectionMatrix(camera, aspect);
  const pickRay = detailed || debugLabel ? createPickingRay(sampleX, sampleY, vp, w, h) : null;
  const debugInput = debug ? [x, y, pickX, pickY, px, py, backingWidth, backingHeight, clientWidth, clientHeight, viewport.x, viewport.y, viewport.width, viewport.height] : null;
  computePickVP(_pickVP, vp, sampleX, sampleY, w, h);
  _pickVP[16] = viewport.x + pixelCenterX;
  _pickVP[17] = viewport.y + pixelCenterY;
  const rt = ensureTargets(engine, picker);
  const detailTarget = detailed ? detailedPicking.ensureDetailTarget(engine, rt) : null;
  const sceneUbo = ensureSceneUbo(engine, picker);
  device.queue.writeBuffer(sceneUbo, 0, _pickVP);
  let nextId = 1;
  const encoder = device.createCommandEncoder({ label: "pick" });
  const colorAttachments = [
    { view: rt.colorView, clearValue: { r: 0, g: 0, b: 0, a: 0 }, loadOp: "clear", storeOp: "store" },
    { view: rt.depthColorView, clearValue: { r: 1, g: 0, b: 0, a: 0 }, loadOp: "clear", storeOp: "store" }
  ];
  if (detailTarget) {
    colorAttachments.push({ view: detailTarget.view, clearValue: { r: 4294967295, g: 0, b: 0, a: 0 }, loadOp: "clear", storeOp: "store" });
  }
  const pass = encoder.beginRenderPass({
    colorAttachments,
    depthStencilAttachment: { view: rt.depthView, depthClearValue: 0, depthLoadOp: "clear", depthStoreOp: "discard" }
  });
  const tempBuffers = [];
  const detailedPositions = detailed ? /* @__PURE__ */ new Map() : null;
  const detailedNormals = detailed ? /* @__PURE__ */ new Map() : null;
  const detailedPoses = deformedVertex?.captureDeformPoses(candidates) ?? null;
  let meshRanges;
  if (advancedDraw) {
    const result = advancedDraw.draw(pass, picker._sceneBG, nextId, pickDiscard, detailed, detailedPicking, tempBuffers, detailedPositions, detailedNormals);
    nextId = result.nextId;
    meshRanges = result.ranges;
  } else {
    meshRanges = [];
    for (const { mesh } of candidates) {
      const gpu = mesh._gpu;
      const projection = deformProjection?.getDeformPickingProjection(engine, mesh) ?? null;
      const defaults = pipelineApi.getPickingPipelineSet(engine, null, projection);
      const discarded = pickDiscard ? pipelineApi.getPickingPipelineSet(engine, pickDiscard, projection) : null;
      if (detailedPositions && mesh._cpuPositions) {
        detailedPositions.set(mesh, mesh._cpuPositions);
      }
      if (detailedNormals && mesh._cpuNormals) {
        detailedNormals.set(mesh, mesh._cpuNormals);
      }
      const discardBG = pickDiscard && discarded?.discardBGL ? createPickDiscardBindGroup(engine, discarded.discardBGL, pickDiscard, mesh, tempBuffers) : null;
      const set = discarded && (!discarded.discardBGL || discardBG) ? discarded : defaults;
      _uboF32.set(mesh.worldMatrix, 0);
      _uboU32[16] = nextId;
      const ubo = createUniformBuffer(engine, _uboView, "pick-mesh-ubo");
      tempBuffers.push(ubo);
      pass.setPipeline(set.regularPipeline);
      pass.setBindGroup(0, picker._sceneBG);
      pass.setBindGroup(
        1,
        device.createBindGroup({
          layout: set.regularPipeline.getBindGroupLayout(1),
          entries: [{ binding: 0, resource: { buffer: ubo } }]
        })
      );
      if (discardBG) {
        pass.setBindGroup(2, discardBG);
      }
      pass.setVertexBuffer(0, gpu.positionBuffer);
      if (projection) {
        deformProjection.bindDeformPickingProjection(engine, pass, set.regularPipeline, mesh, 1, !!discardBG);
      }
      pass.setIndexBuffer(gpu.indexBuffer, gpu.indexFormat);
      pass.drawIndexed(gpu.indexCount);
      meshRanges.push({
        base: nextId++,
        count: 1,
        mesh,
        thin: false,
        world: detailedPicking ? detailedPicking.copyDetailedWorldMatrix(mesh.worldMatrix) : null,
        thinVersion: 0,
        worldAdjusted: false
      });
    }
  }
  const contribRanges = [];
  if (preparedContributors.length > 0) {
    const pickCtx = { picker, pass, engine, scene, camera, sceneBG: picker._sceneBG, px: sampleX, py: sampleY, w, h, detailed };
    for (const { source, contributor } of preparedContributors) {
      if (!scene._pickSources.includes(source)) {
        continue;
      }
      const base = nextId;
      nextId = contributor.draw(pickCtx, base);
      if (nextId > base) {
        contribRanges.push({ base, count: nextId - base, contributor });
      }
    }
  }
  pass.end();
  encoder.copyTextureToBuffer({ texture: rt.colorTex }, { buffer: rt.colorStaging, bytesPerRow: 256 }, { width: 1, height: 1 });
  encoder.copyTextureToBuffer({ texture: rt.depthColorTex }, { buffer: rt.depthStaging, bytesPerRow: 256 }, { width: 1, height: 1 });
  if (detailTarget) {
    detailedPicking.copyDetailTarget(encoder, detailTarget);
  }
  device.queue.submit([encoder.finish()]);
  let pickId;
  let depth;
  let primitiveIndex = -1;
  let localPoint = null;
  try {
    const maps = [rt.colorStaging.mapAsync(GPUMapMode.READ), rt.depthStaging.mapAsync(GPUMapMode.READ)];
    const detailRead = detailTarget ? detailedPicking.readDetailTarget(detailTarget) : null;
    if (detailRead) {
      maps.push(detailRead.then(() => void 0));
    }
    await Promise.all(maps);
    const colorData = new U8(rt.colorStaging.getMappedRange());
    pickId = colorData[0] << 16 | colorData[1] << 8 | colorData[2];
    depth = new F32(rt.depthStaging.getMappedRange())[0];
    if (detailRead) {
      ({ primitiveIndex, localPoint } = await detailRead);
    }
    rt.colorStaging.unmap();
    rt.depthStaging.unmap();
  } finally {
    for (let i = 0; i < tempBuffers.length; i++) {
      tempBuffers[i].destroy();
    }
  }
  if (pickId === 0) {
    if (debug) {
      debug.tracePick(debugLabel, debugInput, pickRay, pickId, depth, false);
    }
    return createEmptyPickingInfo();
  }
  let hitMesh = null;
  let hitRange = null;
  let hitThinIdx = -1;
  for (let i = 0; i < meshRanges.length; i++) {
    const range = meshRanges[i];
    if (pickId >= range.base && pickId < range.base + range.count) {
      hitMesh = range.mesh;
      hitRange = range;
      hitThinIdx = range.thin ? pickId - range.base : -1;
      break;
    }
  }
  let hitContributor = null;
  let contribLocalId = -1;
  if (!hitMesh) {
    for (let ri = 0; ri < contribRanges.length; ri++) {
      const r = contribRanges[ri];
      if (pickId >= r.base && pickId < r.base + r.count) {
        hitContributor = r.contributor;
        contribLocalId = pickId - r.base;
        break;
      }
    }
  }
  if (!hitMesh && !hitContributor) {
    if (debug) {
      debug.tracePick(debugLabel, debugInput, pickRay, pickId, depth, false, true);
    }
    return createEmptyPickingInfo();
  }
  const info = createEmptyPickingInfo();
  info.hit = true;
  info.pickedMesh = hitMesh;
  info.thinInstanceIndex = hitThinIdx;
  info.ray = detailed ? pickRay : null;
  const invVP = mat4Invert(vp);
  if (invVP) {
    const ndcX = 2 * sampleX / w - 1;
    const ndcY = 1 - 2 * sampleY / h;
    const wx = invVP[0] * ndcX + invVP[4] * ndcY + invVP[8] * depth + invVP[12];
    const wy = invVP[1] * ndcX + invVP[5] * ndcY + invVP[9] * depth + invVP[13];
    const wz = invVP[2] * ndcX + invVP[6] * ndcY + invVP[10] * depth + invVP[14];
    const ww = invVP[3] * ndcX + invVP[7] * ndcY + invVP[11] * depth + invVP[15];
    const invW = 1 / ww;
    info.pickedPoint = [wx * invW, wy * invW, wz * invW];
    const origin = detailed && pickRay ? { x: pickRay.origin[0], y: pickRay.origin[1], z: pickRay.origin[2] } : getCameraPosition(camera);
    const dx = info.pickedPoint[0] - origin.x;
    const dy = info.pickedPoint[1] - origin.y;
    const dz = info.pickedPoint[2] - origin.z;
    info.distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
  if (hitContributor) {
    hitContributor.resolve(info, contribLocalId);
  } else if (hitMesh && hitRange?.world && localPoint && primitiveIndex >= 0) {
    const thinStateStable = !hitRange.thin || hitMesh.thinInstances?._version === hitRange.thinVersion;
    if (thinStateStable) {
      const positions = detailedPositions?.get(hitMesh);
      const world = detailedPicking.detailedWorldMatrix(hitRange.world, hitMesh, hitThinIdx);
      const deformTriangle = deformedVertex?.deformerFor(detailedPoses, hitMesh) ?? null;
      detailedPicking.populateDetailedMeshInfo(
        info,
        hitMesh,
        primitiveIndex,
        localPoint,
        positions,
        detailedNormals?.get(hitMesh),
        world,
        !hitRange.worldAdjusted,
        deformTriangle
      );
    }
  }
  if (debug) {
    debug.tracePick(debugLabel, debugInput, info.ray ?? pickRay, pickId, depth, true, false, hitMesh ? hitMesh.name ?? "(unnamed)" : "(contributor)", hitThinIdx, info);
  }
  return info;
}
function pickAsync(picker, x, y, options) {
  const prior = picker._pending ?? Promise.resolve();
  const run = prior.then(
    () => pickAsyncImpl(picker, x, y, options),
    () => pickAsyncImpl(picker, x, y, options)
    // a prior pick's rejection must not wedge the queue for this caller
  );
  picker._pending = run.then(
    () => void 0,
    () => void 0
  );
  return run;
}
function disposePicker(picker) {
  if (picker._rt) {
    for (const resource of [
      picker._rt.colorTex,
      picker._rt.depthColorTex,
      picker._rt.detail?.texture,
      picker._rt.depthTex,
      picker._rt.colorStaging,
      picker._rt.depthStaging,
      picker._rt.detail?.staging
    ]) {
      resource?.destroy();
    }
    picker._rt = null;
  }
  picker._sceneUbo?.destroy();
  picker._sceneUbo = null;
  picker._sceneBG = null;
  if (picker._contributors) {
    for (const contributor of picker._contributors.values()) {
      contributor.dispose?.();
    }
    picker._contributors = null;
  }
  picker._device = null;
}

export { createGpuPicker, disposePicker, pickAsync };
//# sourceMappingURL=gpu-picker.js.map
